import React, { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../lib/api'
import { improveAiErrorUserMessage } from '../lib/improveAiError'
import toast from 'react-hot-toast'
import { Loader2, Plus, Sparkles, X, CheckCircle2, AlertCircle } from 'lucide-react'
import CompetencyDetailsModal from './CompetencyDetailsModal'

type CompetencyStage = 'I' | 'II' | 'III'

const COMPETENCY_STAGE_OPTIONS: Array<{ value: CompetencyStage; label: string }> = [
  { value: 'I', label: 'I Reproduktion' },
  { value: 'II', label: 'II Transfer' },
  { value: 'III', label: 'III Reflexion' }
]

export interface CompetencyAssignment {
  id: number;
  competency_id: number;
  competency_name: string;
  competency_description?: string;
  competency_stage?: CompetencyStage;
  weight: number;
  auto_assigned: boolean;
  confidence_score?: number;
  assignment_source: string;
  notes?: string;
}

interface CompetencySelectorProps {
  entityType: 'building_block' | 'learning_plan' | 'task'
  entityId: number
  curriculumIds?: number[] // Multi-Curriculum Support
  onUpdate?: (assignments?: CompetencyAssignment[]) => void
  requireCurriculumForAutoAssign?: boolean
}

export default function CompetencySelector({
  entityType,
  entityId,
  curriculumIds = [],
  onUpdate,
  requireCurriculumForAutoAssign = true
}: CompetencySelectorProps) {
  const [assignments, setAssignments] = useState<CompetencyAssignment[]>([])
  const [allCompetencies, setAllCompetencies] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [analyzing, setAnalyzing] = useState(false)
  const [showSelect, setShowSelect] = useState(false)
  const [selectedCompetencyIds, setSelectedCompetencyIds] = useState<number[]>([])
  const [selectedCompetencyStages, setSelectedCompetencyStages] = useState<Record<number, CompetencyStage>>({})
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [activeAssignment, setActiveAssignment] = useState<CompetencyAssignment | null>(null)
  
  // Use ref to store onUpdate callback to avoid dependency issues
  const onUpdateRef = useRef(onUpdate)
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  // Fetch assignments
  const fetchAssignments = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}): Promise<CompetencyAssignment[]> => {
    try {
        if (!silent) {
          setLoading(true)
        }
        const response = await api.get(`/competency-assignments/${entityType}/${entityId}`)
      if (response.data.success) {
          const assignmentsData: CompetencyAssignment[] = response.data.data || []
          setAssignments(assignmentsData)
          onUpdateRef.current?.(assignmentsData)
          return assignmentsData
      }
        setAssignments([])
        onUpdateRef.current?.([])
        return []
    } catch (error: any) {
        console.error('Error fetching assignments:', error)
        toast.error('Fehler beim Laden der Kompetenzen')
        setAssignments([])
        onUpdateRef.current?.([])
        return []
    } finally {
        if (!silent) {
          setLoading(false)
        }
    }
    },
    [entityType, entityId]
  )

  // Fetch competencies based on selected curricula
  const fetchAllCompetencies = useCallback(async () => {
    try {
      // If curricula selected, fetch only those, otherwise fetch all
      if (curriculumIds && curriculumIds.length > 0) {
        const allComps: any[] = [];
        for (const curriculumId of curriculumIds) {
          const response = await api.get(`/curricula/${curriculumId}/competencies`);
          if (response.data.success) {
            allComps.push(...response.data.data);
          }
        }
        setAllCompetencies(allComps);
      } else {
        // Fallback: fetch all competencies
        const response = await api.get('/competencies');
        if (response.data.success) {
          setAllCompetencies(response.data.data || []);
        }
      }
    } catch (error: any) {
      console.error('Error fetching competencies:', error);
    }
  }, [curriculumIds]);

  useEffect(() => {
    fetchAssignments();
    fetchAllCompetencies();
  }, [fetchAssignments, fetchAllCompetencies]);

  // Auto-assign with LLM for multiple curricula
  const handleAutoAssign = async () => {
    if (!curriculumIds || curriculumIds.length === 0) {
      toast.error('Mindestens ein Curriculum auswählen für automatische Zuordnung');
      return;
    }

    try {
      setAnalyzing(true);
      const results = [];
      
      for (const curriculumId of curriculumIds) {
        toast.loading(`Analysiere Inhalte für Curriculum ${curriculumId}...`, { id: `analyze-${curriculumId}` });
        
        const response = await api.post(
          `/competency-assignments/${entityType}/${entityId}/auto-assign`,
          { curriculumId, autoSave: true }
        );

        toast.dismiss(`analyze-${curriculumId}`);

        if (response.data.success) {
          results.push({
            curriculumId,
            count: response.data.totalAssigned || 0
          });
        }
      }

      const totalCount = results.reduce((sum, r) => sum + r.count, 0)
      toast.success(`${totalCount} Kompetenz(en) automatisch zugeordnet (${curriculumIds.length} Curricula)`)
      setShowSelect(false)
      await fetchAssignments({ silent: true })
    } catch (error: any) {
      console.error('Error in auto-assign:', error);
      toast.error(
        improveAiErrorUserMessage(error, 'Fehler bei der automatischen Zuordnung')
      )
    } finally {
      setAnalyzing(false);
    }
  };

  // Toggle competency selection
  const toggleCompetencySelection = (competencyId: number) => {
    setSelectedCompetencyIds(prev => {
      const isSelected = prev.includes(competencyId)
      if (isSelected) {
        setSelectedCompetencyStages((current) => {
          const next = { ...current }
          delete next[competencyId]
          return next
        })
        return prev.filter(id => id !== competencyId)
      }
      setSelectedCompetencyStages((current) => ({
        ...current,
        [competencyId]: current[competencyId] || 'I'
      }))
      return [...prev, competencyId]
    })
  }

  const handleSelectedCompetencyStageChange = (competencyId: number, stage: CompetencyStage) => {
    setSelectedCompetencyStages((prev) => ({
      ...prev,
      [competencyId]: stage
    }))
  }

  // Select all available competencies
  const handleSelectAll = () => {
    if (selectedCompetencyIds.length === availableCompetencies.length) {
      setSelectedCompetencyIds([])
      setSelectedCompetencyStages({})
    } else {
      const ids = availableCompetencies.map(comp => comp.id)
      setSelectedCompetencyIds(ids)
      const stageMap: Record<number, CompetencyStage> = {}
      ids.forEach((id) => {
        stageMap[id] = selectedCompetencyStages[id] || 'I'
      })
      setSelectedCompetencyStages(stageMap)
    }
  }

  // Manual assign multiple competencies
  const handleManualAssign = async () => {
    if (selectedCompetencyIds.length === 0) {
      toast.error('Bitte mindestens eine Kompetenz auswählen');
      return;
    }

    try {
      let successCount = 0
      let errorCount = 0

      // Assign all selected competencies
      for (const competencyId of selectedCompetencyIds) {
        const selectedStage = selectedCompetencyStages[competencyId] || 'I'
        const alreadyExists = assignments.some(
          (a) => a.competency_id === competencyId && (a.competency_stage || 'I') === selectedStage
        )
        if (alreadyExists) {
          continue
        }
        try {
          const response = await api.post(
            `/competency-assignments/${entityType}/${entityId}`,
            {
              competencyId,
              competencyStage: selectedStage
            }
          );

          if (response.data.success) {
            successCount++
          } else {
            errorCount++
          }
        } catch (error: any) {
          console.error(`Error assigning competency ${competencyId}:`, error);
          errorCount++
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} Kompetenz(en) zugeordnet${errorCount > 0 ? ` (${errorCount} Fehler)` : ''}`);
        setSelectedCompetencyIds([]);
        setSelectedCompetencyStages({});
        setShowSelect(false);
        await fetchAssignments();
      } else {
        toast.error('Fehler beim Zuordnen der Kompetenzen');
      }
    } catch (error: any) {
      console.error('Error in manual assign:', error);
      toast.error(error.response?.data?.message || 'Fehler beim Zuordnen');
    }
  };

  // Remove assignment
  const handleRemove = async (competencyId: number, competencyStage: CompetencyStage = 'I') => {
    try {
      const response = await api.delete(
        `/competency-assignments/${entityType}/${entityId}/${competencyId}`,
        {
          data: {
            competency_stage: competencyStage
          }
        }
      );

      if (response.data.success) {
        toast.success('Kompetenz entfernt');
        await fetchAssignments();
      }
    } catch (error: any) {
      console.error('Error removing assignment:', error);
      toast.error('Fehler beim Entfernen');
    }
  };

  const handleAssignmentStageChange = async (assignment: CompetencyAssignment, stage: CompetencyStage) => {
    const currentStage = assignment.competency_stage || 'I'
    if (currentStage === stage) {
      return
    }
    try {
      const response = await api.patch(
        `/competency-assignments/${entityType}/${entityId}/${assignment.competency_id}`,
        {
          competency_stage: stage,
          current_competency_stage: assignment.competency_stage || 'I'
        }
      )
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Stufe konnte nicht gespeichert werden')
      }
      toast.success('Stufe aktualisiert')
      await fetchAssignments({ silent: true })
    } catch (error: any) {
      console.error('Error updating competency stage:', error)
      toast.error(error.response?.data?.message || 'Fehler beim Speichern der Stufe')
    }
  }

  const availableCompetencies = allCompetencies

  const openDetailsModal = (assignment: CompetencyAssignment) => {
    setActiveAssignment(assignment)
    setShowDetailsModal(true)
  }

  const closeDetailsModal = useCallback(() => {
    setShowDetailsModal(false)
    setActiveAssignment(null)
  }, [])

  useEffect(() => {
    if (!showDetailsModal || !activeAssignment) return
    const stillExists = assignments.some(
      (assignmentItem) => assignmentItem.competency_id === activeAssignment.competency_id
    )
    if (!stillExists) {
      closeDetailsModal()
    }
  }, [activeAssignment, assignments, closeDetailsModal, showDetailsModal])

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Lade Kompetenzen...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Zuordnung-Chips */}
      {assignments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {assignments.map(assignment => (
            <div
              key={assignment.id}
              className="group relative inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm cursor-pointer dark:bg-blue-950/50 dark:border-blue-700"
              title={assignment.notes || assignment.competency_description || ''}
              role="button"
              tabIndex={0}
              onClick={() => openDetailsModal(assignment)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  openDetailsModal(assignment)
                }
              }}
            >
              {/* Auto-Assign Indicator */}
              {assignment.auto_assigned && (
                <div className="relative">
                  <Sparkles className="h-3 w-3 text-purple-500" />
                  {assignment.confidence_score && (
                    <span className="absolute -top-1 -right-1 text-[10px] font-bold text-purple-600">
                      {Math.round(assignment.confidence_score)}%
                    </span>
                  )}
                </div>
              )}

              <span className="font-medium text-blue-900 dark:text-blue-100">
                {assignment.competency_name}
              </span>

              <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-200">
                {assignment.competency_stage || 'I'}
              </span>

              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/60 dark:text-blue-200">
                {assignment.weight !== undefined && assignment.weight !== null
                  ? Number(assignment.weight).toFixed(2)
                  : '—'}
              </span>

              <select
                value={assignment.competency_stage || 'I'}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation()
                  handleAssignmentStageChange(assignment, e.target.value as CompetencyStage)
                }}
                className="rounded border border-indigo-300 bg-white px-1.5 py-0.5 text-[11px] text-indigo-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-indigo-600 dark:bg-gray-800 dark:text-indigo-200"
                title="Stufe ändern"
              >
                {COMPETENCY_STAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.value}
                  </option>
                ))}
              </select>

              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemove(assignment.competency_id, assignment.competency_stage || 'I')
                }}
                className="opacity-0 transition-opacity group-hover:opacity-100"
              >
                <X className="h-4 w-4 text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        {/* Manual Add Button */}
        {!showSelect ? (
          <button
            onClick={() => setShowSelect(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-sm font-medium transition-colors dark:bg-gray-800 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          >
            <Plus className="h-4 w-4" />
            Kompetenzen hinzufügen
          </button>
        ) : (
          <div className="flex-1">
            <div className="border border-gray-300 rounded-lg bg-white dark:border-gray-600 dark:bg-gray-900">
              <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Kompetenzen auswählen
                  </span>
                  {selectedCompetencyIds.length > 0 && (
                    <span className="text-xs text-blue-600 font-medium">
                      {selectedCompetencyIds.length} ausgewählt
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {availableCompetencies.length > 0 && (
                    <button
                      onClick={handleSelectAll}
                      className="text-xs text-gray-600 hover:text-gray-800"
                    >
                      {selectedCompetencyIds.length === availableCompetencies.length ? 'Alle abwählen' : 'Alle auswählen'}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedCompetencyIds([])
                      setSelectedCompetencyStages({})
                      setShowSelect(false);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Fertig
                  </button>
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto p-2">
                {availableCompetencies.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500">
                    Alle Kompetenzen bereits zugeordnet
                  </div>
                ) : (
                  <div className="space-y-1">
                    {availableCompetencies.map(comp => (
                      <label
                        key={comp.id}
                        className="flex items-start space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCompetencyIds.includes(comp.id)}
                          onChange={() => toggleCompetencySelection(comp.id)}
                          className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="text-sm text-gray-700 dark:text-gray-200">{comp.name}</span>
                          {comp.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{comp.description.substring(0, 100)}...</p>
                          )}
                          {selectedCompetencyIds.includes(comp.id) && (
                            <div className="mt-1 flex items-center gap-2">
                              <span className="text-[11px] text-gray-500">Stufe:</span>
                              <select
                                value={selectedCompetencyStages[comp.id] || 'I'}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation()
                                  handleSelectedCompetencyStageChange(comp.id, e.target.value as CompetencyStage)
                                }}
                                className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                              >
                                {COMPETENCY_STAGE_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {selectedCompetencyIds.length > 0 && (
                <div className="p-2 border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
                  <button
                    onClick={handleManualAssign}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {selectedCompetencyIds.length} Kompetenz(en) hinzufügen
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Auto-Assign Button */}
        {requireCurriculumForAutoAssign && curriculumIds && curriculumIds.length > 0 && (
          <button
            onClick={handleAutoAssign}
            disabled={analyzing}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {analyzing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Analysiert...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Automatisch zuordnen
              </>
            )}
          </button>
        )}

        {requireCurriculumForAutoAssign && (!curriculumIds || curriculumIds.length === 0) && (
          <div className="flex items-center gap-2 px-3 py-1.5 text-gray-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>Curriculum(e) auswählen</span>
          </div>
        )}
      </div>

      {/* Info */}
      {assignments.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
          Noch keine Kompetenzen zugeordnet
        </p>
      )}

      <CompetencyDetailsModal
        isOpen={showDetailsModal}
        entityType={entityType}
        entityId={entityId}
        assignment={activeAssignment}
        onClose={closeDetailsModal}
        onRefreshAssignments={() => fetchAssignments({ silent: true })}
      />
    </div>
  );
}

